import { Injectable } from '@angular/core';
import { Dictionary } from '@ngrx/entity';
import { select, Store } from '@ngrx/store';
import {
    CpRootNode,
    CustomPagePathDescriptionVisibility,
    ICustomPage,
    IDetailPanelItem,
    IDynamicPanelItem,
    IFixedPanelItem,
    IHorizontalPanelItem,
    isVirtualPanel,
    IUserControl,
    IVirtualPanelItem,
} from '@se/cp.common';
import { seToolbarSetRightSlider } from '@se/platform';
import { DataBinding, Guid, Logger } from '@se/se.platform.base';
import { cloneDeep, isEmpty, merge, negate } from 'lodash-es';
import { BehaviorSubject, map, Observable } from 'rxjs';
import { delay, distinctUntilChanged } from 'rxjs/operators';
import { CpControlGrid } from './cp-configuration/utils/control-grid';
import { isNotNullOrUndefined } from './cp-configuration/utils/utils';
import { CpControlType, CpTextEditorControl } from './cp-configuration/view-model';

import { CpFlatNode, CpHorizontalNode, cpHorizontalNodeFromPanel, cpNodeFromPanel } from './cp-node';
import {
    cpAddNodes,
    cpAddNodesToRoot,
    cpAddPanel,
    cpInitializeNodes,
    cpInitializePanelsService,
    cpRefreshNodesStatus,
    cpRemoveNodes,
    cpSelectNode,
    cpUpdateNode,
    cpUpdateNodes
} from './store/cp.actions';
import { selectCpAllNodes, selectCpNodeSlice, selectCpSelectedNode } from './store/cp.selectors';

import * as fromCp from './store/cp.state';

export interface IDetailNode {
    id: string;
    header: string;
    startsAsExpanded: boolean;
    pathDescriptionVisibility: CustomPagePathDescriptionVisibility;
    horizontalPanelItems: string[];
    pathDescription: string;
}

/** Cambiare nome interfaccia -> uscita del metodo che crea le componenti da renderizzare */
interface ControlLists {
    controls: Dictionary<CpControlGrid>; // tutti gli input ? ci sono anche quelli virtuali? chiave === IDPannello padre
    horizontals: Dictionary<CpHorizontalNode>; // pannelli orizzontali
    details: Dictionary<IDetailNode>; // pannelli di dettaglio
    nodes: CpFlatNode[]; // pannelli dinamici
    virtualPanels: Dictionary<IDynamicPanelItem>; // pannelli virtuali
    userControls: Dictionary<IUserControl>; // user controls
}

function getInitialLists(): ControlLists {
    return {
        controls: {},
        horizontals: {},
        details: {},
        nodes: [],
        virtualPanels: {},
        userControls: {},
    };
}

@Injectable({
    providedIn: 'root',
})
export class CpNodeService {
    /**
     * Dizionario per id dei singoli controlli.
     */
    controlList: Dictionary<CpControlGrid> = {};

    /**
     * Dizionario dei pannelli di dettaglio.
     */
    detailsList: Dictionary<IDetailNode> = {};

    /**
     * Dizionario degli UserControl
     */
    userControlList: Dictionary<IUserControl> = {};

    /**
     * Lista flat dei nodi fixed.
     * Non si trova nello stato perché non cambia.
     */
    private fixedNodeList = new BehaviorSubject<CpFlatNode[]>([]);

    /**
     * Lista flat dei nodi orizzontali.
     */
    private horizontalNodeList: Dictionary<CpHorizontalNode> = {};

    /**
     * Dizionario chiave-valore dei nodi e dei pannelli virtuali.
     */
    private virtualPanels: Dictionary<IDynamicPanelItem> = {};

    /**
     * Stato dei nodi della custom page.
     */
    private nodes: fromCp.CpNodeState = {
        ids: [],
        entities: {},
        root: { autoNormalValues: false, description: '', id: '', idAddMenuDataSource: '', name: '' },
    };

    constructor(
        private logger: Logger,
        private store: Store<fromCp.RootState>,
    ) {
        this.store.pipe(select(selectCpNodeSlice)).subscribe((nodes) => {
            this.nodes = nodes;
        });
    }

    hasFixedNodes$() {
        return this.fixedNodeList.pipe(map(negate(isEmpty)));
    }

    getFixedNodeList() {
        return this.fixedNodeList.asObservable();
    }

    getFlatNodeList() {
        return this.store.pipe(select(selectCpAllNodes), distinctUntilChanged(), delay(0));
    }

    getAllControlsList(): Observable<CpControlType[]> {
        return this.store.pipe(select(selectCpAllNodes), distinctUntilChanged()).pipe(
            map((flatNodes) =>
                flatNodes
                    .map((node) => {
                        const detailsControls = node.detailIds.map((detailId) => this.controlList[detailId]);

                        const horizontalIds = [
                            ...node.horizontalIds,
                            ...node.detailIds
                                .map((detailId) => this.detailsList[detailId])
                                .filter((detailPanel) => detailPanel && detailPanel.horizontalPanelItems?.length > 0)
                                .map((detailPanel) => detailPanel && detailPanel.horizontalPanelItems)
                                .flat(),
                        ];

                        const horizontalControls = horizontalIds
                            .filter(isNotNullOrUndefined)
                            .map((horizontalId) => this.controlList[horizontalId]);

                        return [this.controlList[node.id], ...detailsControls, ...horizontalControls].filter(
                            Boolean,
                        ) as unknown as CpControlGrid[];
                    })
                    .flat()
                    .map((grid) => {
                        return grid.getAllControls();
                    })
                    .flat(),
            ),
        );
    }

    getAllTextEditorControls(): Observable<CpTextEditorControl[]> {
        return this.getAllControlsList().pipe(
            map((controls) =>
                controls.filter((control): control is CpTextEditorControl => control instanceof CpTextEditorControl),
            ),
        );
    }

    getAllNodeChildrenIds = (): string[] => [];

    getAllControlByNode = (nodeId: string): CpControlType[] => {
        const currentNode = this.nodes.entities[nodeId];
        if (!currentNode) return [];
        const childControls = currentNode.children.map((childNodeId) => this.getAllControlByNode(childNodeId)).flat();
        const currentControls = this.controlList[nodeId]?.getAllControls() ?? [];
        const detailControls = currentNode.detailIds
            .map((detailId) => this.controlList[detailId])
            .filter(isNotNullOrUndefined)
            .map((grid) => grid.getAllControls())
            .flat();
        const horizontalIds = [
            ...currentNode.horizontalIds,
            ...currentNode.detailIds
                .map((detailId) => this.detailsList[detailId])
                .filter((detailPanel) => detailPanel && detailPanel.horizontalPanelItems?.length > 0)
                .map((detailPanel) => detailPanel && detailPanel.horizontalPanelItems)
                .flat()
                .filter(isNotNullOrUndefined),
        ];
        const horizontalControls = horizontalIds
            .map((horizontalId) => this.controlList[horizontalId])
            .filter(isNotNullOrUndefined)
            .map((grid) => grid.getAllControls())
            .flat();
        return [...currentControls, ...detailControls, ...horizontalControls, ...childControls];
    };

    getNodeById(id: string) {
        return this.nodes.entities[id];
    }

    getSelectedNode(): Observable<string | null> {
        return this.store.pipe(select(selectCpSelectedNode));
    }

    getFirstChildOf(node: CpFlatNode) {
        const child = this.nodes.entities[node.id]?.children[0];

        return child ? this.nodes.entities[child] : undefined;
    }

    getUserControlFromNode(node: CpFlatNode): IUserControl | undefined {
        return this.userControlList[node.id];
    }

    getControlGridFromNode(id: string): CpControlGrid {
        return this.controlList[id] ?? CpControlGrid.empty();
    }

    getControlGridFromVirtualNode(id: string, dataBindingContext?: DataBinding): CpControlGrid {
        const panel = this.virtualPanels[id];
        if (!panel) {
            return CpControlGrid.empty();
        }

        return CpControlGrid.fromPanel(panel, dataBindingContext);
    }

    getVirtualById(id: string): IVirtualPanelItem | undefined {
        return this.virtualPanels[id] as IVirtualPanelItem;
    }

    getDetailsById(id: string) {
        return this.detailsList[id];
    }

    getHorizontalNodeById(id: string) {
        return this.horizontalNodeList[id];
    }

    hydrateVirtualNode(
        parentId: string | undefined,
        virtualPanel: IVirtualPanelItem,
        createdBy: string,
        dataBindingContext?: DataBinding,
    ): CpFlatNode | undefined {
        const parent = parentId ? this.nodes.entities[parentId] : undefined;
        const panel = cloneDeep(virtualPanel); //IDynamicPanelItem
        this.recreatePanelIds(panel); // ricrea id pannello virtuale e id pannelli figli del virtuale

        panel.createdBy = createdBy;

        const { controls, details, horizontals, nodes, userControls, virtualPanels } = this.walkTree(
            panel,
            getInitialLists(),
            dataBindingContext ?? null,
            true,
            parent ? parent.level + 1 : 0,
            '',
            parent,
        );
        this.controlList = merge(this.controlList, controls);
        this.detailsList = merge(this.detailsList, details);
        this.horizontalNodeList = merge(this.horizontalNodeList, horizontals);
        this.userControlList = merge(this.userControlList, userControls);
        this.virtualPanels = merge(this.virtualPanels, virtualPanels);

        if (parentId) {
            this.store.dispatch(cpAddNodes({ nodes, parentId }));
        } else {
            this.store.dispatch(cpAddNodesToRoot({ nodes }));
        }

        return nodes.find((node) => node.id === panel.id);
    }

    removeNodesById(...nodes: string[]) {
        if (isEmpty(nodes)) {
            return;
        }

        this.store.dispatch(cpRemoveNodes({ nodes }));
    }

    updateNodeCreatedBy(nodeAndEntityIds: [string, string][]) {
        this.store.dispatch(
            cpUpdateNodes({
                nodes: nodeAndEntityIds.map(([n, e]) => ({ id: n, changes: { createdBy: e } })),
            }),
        );
    }

    initialize(layout: ICustomPage) {
        const rootNode = this.createRootNode(layout);
        const { controls, details, horizontals, nodes, virtualPanels, userControls } = layout.dynamicPanelItems.reduce(
            (state, panel) => this.walkTree(panel, state, null),
            getInitialLists(),
        );

        const fixedNodeLists = this.buildFixedNodeList(layout.fixedPanelItems);
        this.fixedNodeList.next(fixedNodeLists.nodes);
        if (fixedNodeLists.nodes.length === 0) {
            this.store.dispatch(
                seToolbarSetRightSlider({
                    status: {
                        checked: true,
                        enabled: false,
                        visible: false,
                        overlap: false,
                    },
                }),
            );
        }

        this.controlList = merge(controls, fixedNodeLists.controls);
        this.horizontalNodeList = horizontals;
        this.detailsList = merge(details, fixedNodeLists.details);
        this.userControlList = merge(userControls, fixedNodeLists.userControls);
        this.virtualPanels = virtualPanels;

        this.store.dispatch(cpInitializeNodes({ nodes, rootNode }));
        this.store.dispatch(cpInitializePanelsService());
        this.store.dispatch(cpRefreshNodesStatus());
    }

    /*
     * Creo l'oggetto root node compresa la collection di voci da mettere in add se esistono
     */
    createRootNode(layout: ICustomPage): CpRootNode {
        return {
            id: layout.id,
            autoNormalValues: layout.autoNormalValues,
            description: layout.description,
            idAddMenuDataSource: layout.idAddMenuDataSource,
            name: layout.name,
        };
    }

    safeExit() {
        this.controlList = {};
        this.detailsList = {};
        this.userControlList = {};
        this.virtualPanels = {};
        this.store.dispatch(cpInitializeNodes({ nodes: [] }));
        this.fixedNodeList.next([]);
    }

    selectNode(node: CpFlatNode) {
        this.store.dispatch(cpSelectNode({ id: node.id }));
    }

    changeNodeHeader(id: string, header: string) {
        if (!this.nodes.entities[id] || this.nodes.entities[id]?.header === header) return;
        this.store.dispatch(cpUpdateNode({ id, changes: { header } }));
    }

    changeNodeModifiedStatus(id: string) {
        if (!this.nodes.entities[id] || this.nodes.entities[id]?.isModified) return;
        this.store.dispatch(cpUpdateNode({ id, changes: { isModified: true } }));
    }

    changeNodeHasAIDataStatus(id: string, hasAIData: boolean) {
        if (!this.nodes.entities[id] || this.nodes.entities[id]?.hasAIData === hasAIData) return;
        this.store.dispatch(cpUpdateNode({ id, changes: { hasAIData } }));
    }

    changeNodeWarningStatus(id: string, warnings: string[]) {
        if (!this.nodes.entities[id] || (this.nodes.entities[id]?.warnings.length === 0 && warnings.length === 0))
            return;
        const prevWarningsSet = new Set(this.nodes.entities[id]?.warnings);
        const currentWarningSet = new Set(warnings);
        if (!this.areSetsEqual(prevWarningsSet, currentWarningSet)) {
            this.store.dispatch(cpUpdateNode({ id, changes: { warnings } }));
        }
    }

    areSetsEqual<T>(setA: Set<T>, setB: Set<T>): boolean {
        if (setA.size !== setB.size) return false;
        for (const item of setA) {
            if (!setB.has(item)) return false;
        }
        return true;
    }

    changeNodeAutoUpdatedStatus(id: string, isAutoUpdated: boolean) {
        if (!this.nodes.entities[id] || this.nodes.entities[id]?.isAutoUpdated === isAutoUpdated) return;
        this.store.dispatch(cpUpdateNode({ id, changes: { isAutoUpdated } }));
    }

    changeNodeErrorStatus(id: string, isValid: boolean) {
        if (!this.nodes.entities[id] || this.nodes.entities[id]?.isInError === !isValid) return;
        this.store.dispatch(cpUpdateNode({ id, changes: { isInError: !isValid } }));
    }

    changeNodeVisibility(id: string, isVisible: boolean) {
        if (!this.nodes.entities[id] || this.nodes.entities[id]?.isVisible === isVisible) return;
        this.store.dispatch(cpUpdateNode({ id, changes: { isVisible } }));
    }

    // #region Private

    /*
     * Costruisce la struttura flat dei pannelli fissi della Custom Page (non hanno figli)
     */
    private buildFixedNodeList(fixedPanelList: IFixedPanelItem[]): ControlLists {
        if (!fixedPanelList) {
            return getInitialLists();
        }

        return fixedPanelList.reduce((state, panel) => this.walkTree(panel, state, null), getInitialLists());
    }

    /**
     * Rigenera gli id di un pannello e dei suoi figli.
     * Necessario durante l'idratazione di un pannello virtuale.
     */
    private recreatePanelIds(panel: IDynamicPanelItem | IDetailPanelItem | IHorizontalPanelItem) {
        panel.id = new Guid().toString();

        if (panel && 'dynamicPanelItems' in panel) {
            if (panel.detailPanelItems) {
                panel.detailPanelItems.forEach((p) => this.recreatePanelIds(p));
            }
            if (panel.horizontalPanelItems) {
                panel.horizontalPanelItems.forEach((p) => this.recreatePanelIds(p));
            }
            if (panel.dynamicPanelItems) {
                panel.dynamicPanelItems.forEach((p) => this.recreatePanelIds(p));
            }
        }
    }

    // FIXME: Semplificami
    private walkTree(
        panel: IDynamicPanelItem,
        currentState = getInitialLists(),
        dataBindingContext: DataBinding | null,
        hydrating = false,
        level = 0,
        pathDescription = '',
        parent?: CpFlatNode,
    ): ControlLists {
        // Itera il controlPanel e i detailPanelItem per costruire controlli, dettagli e userControl.
        const controlGrid = CpControlGrid.fromPanel(panel, dataBindingContext ?? undefined);
        const [newHorizontals, newHorizontalsControls] = this.buildHorizontals(panel, level, dataBindingContext);

        const newUserControls = this.buildUserControls(panel);

        const node = this.buildNode(panel, hydrating, level, parent, pathDescription); //node.children non contiene panelli virtuali vengono rimossi dalla funzione.

        const [newDetails, newDetailsControls, newDetailsHorizontals] = this.buildDetails(
            panel,
            level,
            dataBindingContext,
            node.pathDescription,
        );

        /**
         * test
         */
        if (isVirtualPanel(panel) && !hydrating) {
            // creo tab selector attraverso il nuovo servizio!
            try {
                this.store.dispatch(cpAddPanel({ parentNodeId: parent?.id ?? '', panel }));
            } catch (e) {
                this.logger.error(e);
            }
        }
        // non va creato un tab selector nuovo se i pannelli virtuali hanno lo stesso padre e stesso binding.

        const newState: ControlLists = {
            controls: merge(
                currentState.controls,
                { [node.id]: controlGrid },
                newDetailsControls,
                newHorizontalsControls,
            ),
            horizontals: merge(currentState.horizontals, newHorizontals, newDetailsHorizontals),
            details: merge(currentState.details, newDetails),
            nodes: node.isVirtual ? currentState.nodes : currentState.nodes.concat(node),
            virtualPanels: node.isVirtual
                ? merge(currentState.virtualPanels, { [node.id]: panel })
                : currentState.virtualPanels,
            userControls: merge(currentState.userControls, newUserControls),
        };
        // Non serve fare altro se non ci sono figli o se non stiamo idratando un pannello virtuale => niente chiamata ricorsiva.
        if (isEmpty(panel.dynamicPanelItems) || node.isVirtual) {
            return newState;
        }

        const descrForChildren =
            node.pathDescriptionVisibility === CustomPagePathDescriptionVisibility.visible
                ? node.pathDescription
                : pathDescription;

        return panel.dynamicPanelItems.reduce(
            // i pannelli virtuali ci sono
            (s, p) => this.walkTree(p, s, dataBindingContext, false, level + 1, descrForChildren, node),
            newState,
        );
    }

    private buildNode(
        panel: IDynamicPanelItem,
        hydrating: boolean,
        level: number,
        parent: CpFlatNode | undefined,
        pathDescription: string,
    ) {
        const node = cpNodeFromPanel(panel); // no figli virtuali.

        node.isVirtual = hydrating ? false : node.isVirtual; // quando questo valore è true???
        node.level = level;
        node.parentId = parent ? parent.id : undefined;

        if (pathDescription) {
            node.pathDescription = `${pathDescription} ► ${node.pathDescription}`;
        }

        return node;
    }

    private buildUserControls(panel: IDynamicPanelItem): Dictionary<IUserControl> {
        return panel.controlPanel && !isEmpty(panel.controlPanel.userControl)
            ? { [panel.controlPanel.userControl.id]: panel.controlPanel.userControl }
            : {};
    }

    private buildDetails(
        panel: IDynamicPanelItem,
        level: number,
        dataBindingContext: DataBinding | null,
        pathDescription: string | null,
    ) {
        let newControls: Dictionary<CpControlGrid> = {};
        let newHorizontals: Dictionary<CpHorizontalNode> = {};
        let newNodes: Dictionary<IDetailNode> = {};

        for (const detail of panel?.detailPanelItems ?? []) {
            const [horizontalNodes, horizontalControls] = this.buildHorizontals(detail, level, dataBindingContext);
            const node: IDetailNode = {
                id: detail.id,
                header: detail.header,
                pathDescriptionVisibility: detail.pathDescriptionVisibility,
                startsAsExpanded: detail.startsAsExpanded,
                horizontalPanelItems: Object.values(horizontalNodes).map((n) => n.id),
                pathDescription: '',
            };
            if (pathDescription) {
                node.pathDescription = `${pathDescription} ► ${node.pathDescription}`;
            }
            newControls[node.id] = CpControlGrid.fromPanel(detail, dataBindingContext ?? undefined);
            newControls = merge(newControls, horizontalControls);
            newHorizontals = merge(newHorizontals, horizontalNodes);
            newNodes[node.id] = node;
            newNodes = merge(newNodes, horizontalNodes);
        }

        return [newNodes, newControls, newHorizontals] as const;
    }

    private buildHorizontals(
        panel: IDynamicPanelItem | IDetailPanelItem,
        level: number,
        dataBindingContext: DataBinding | null,
    ) {
        const newNodes: Record<string, CpHorizontalNode> = {};
        const newControls: Record<string, CpControlGrid> = {};

        for (const horiz of panel?.horizontalPanelItems ?? []) {
            const node: CpHorizontalNode = cpHorizontalNodeFromPanel(horiz, level);
            newNodes[node.id] = node;
            newControls[node.id] = CpControlGrid.fromPanel(horiz, dataBindingContext ?? undefined);
        }

        return [newNodes, newControls] as const;
    }

    // #endregion Private
}
